import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import getApiBaseUrl from "../utils/api";

// ============================================================
// PBT READING DATA
// 5 passages × 5 questions = 25 questions
// ============================================================

const readingModules = [
  {
    id: "pbtReading",
    title: "TOEFL Reading Test",
    sections: [
      {
        id: "passage1",
        type: "academic",
        title: "Photosynthesis",
        passage: `Photosynthesis is the process by which green plants and some other organisms use sunlight to synthesize nutrients from carbon dioxide and water. Photosynthesis in plants generally involves the green pigment chlorophyll and generates oxygen as a by-product. It is critical for life on Earth because it produces the oxygen we breathe and forms the base of most food chains. The process occurs in the chloroplasts, specifically in structures called thylakoids. The light energy is used to split water molecules, releasing oxygen. The hydrogen from water then combines with carbon dioxide to form glucose.`,
        questions: [
          {
            id: 1,
            question: "What is the main topic of the passage?",
            options: [
              "Plant reproduction",
              "Photosynthesis",
              "Cellular respiration",
              "Plant genetics",
            ],
            correct: 1,
          },
          {
            id: 2,
            question:
              "According to the passage, where does photosynthesis occur within a plant cell?",
            options: [
              "In the nucleus",
              "In the mitochondria",
              "In the chloroplasts",
              "In the cell wall",
            ],
            correct: 2,
          },
          {
            id: 3,
            question: "What is released as a by-product of photosynthesis?",
            options: ["Carbon dioxide", "Glucose", "Oxygen", "Hydrogen"],
            correct: 2,
          },
          {
            id: 4,
            question:
              "The word 'synthesize' in line 1 is closest in meaning to...",
            options: ["separate", "create", "destroy", "analyze"],
            correct: 1,
          },
          {
            id: 5,
            question:
              "Why is photosynthesis described as 'critical for life on Earth'?",
            options: [
              "It produces carbon dioxide for plants.",
              "It creates food and oxygen for other organisms.",
              "It regulates global temperatures.",
              "It prevents soil erosion.",
            ],
            correct: 1,
          },
        ],
      },

      {
        id: "passage2",
        type: "academic",
        title: "The Industrial Revolution",
        passage: `The Industrial Revolution was a period of major industrialization that took place during the late 1700s and early 1800s. It began in Great Britain and quickly spread throughout the world. This time period saw the mechanization of agriculture and textile manufacturing, and a revolution in power, including steam ships and railroads, that affected social, cultural and economic conditions. Factories emerged, leading to urbanization as people moved to cities for work. Working conditions were often harsh, prompting the rise of labor movements.`,
        questions: [
          {
            id: 6,
            question: "Where did the Industrial Revolution begin?",
            options: ["United States", "France", "Great Britain", "Germany"],
            correct: 2,
          },
          {
            id: 7,
            question: "Which of the following was NOT mentioned in the passage?",
            options: [
              "Agriculture",
              "Textile manufacturing",
              "Computer programming",
              "Steamships and railroads",
            ],
            correct: 2,
          },
          {
            id: 8,
            question:
              "The word 'urbanization' in line 5 is closest in meaning to...",
            options: [
              "movement to rural areas",
              "growth of cities",
              "population decline",
              "industrial decline",
            ],
            correct: 1,
          },
          {
            id: 9,
            question:
              "According to the passage, what contributed to the rise of labor movements?",
            options: [
              "Improved working conditions",
              "Harsh working conditions",
              "Government regulations",
              "Technological advances",
            ],
            correct: 1,
          },
          {
            id: 10,
            question: "What is the main idea of the passage?",
            options: [
              "The Industrial Revolution had only positive effects.",
              "The Industrial Revolution brought major changes to society and the economy.",
              "The Industrial Revolution began in the United States.",
              "The Industrial Revolution ended in the 1700s.",
            ],
            correct: 1,
          },
        ],
      },

      {
        id: "passage3",
        type: "academic",
        title: "The Solar System",
        passage: `The Solar System consists of the Sun and the objects that orbit it, including eight planets, their moons, and various smaller bodies like asteroids and comets. The four inner planets—Mercury, Venus, Earth, and Mars—are rocky and relatively small. The outer planets—Jupiter, Saturn, Uranus, and Neptune—are gas giants, much larger and composed mainly of hydrogen and helium. The Sun contains 99.8% of the Solar System's mass, exerting gravitational pull that keeps everything in orbit.`,
        questions: [
          {
            id: 11,
            question:
              "What percentage of the Solar System's mass is contained in the Sun?",
            options: ["90%", "95%", "99.8%", "100%"],
            correct: 2,
          },
          {
            id: 12,
            question: "Which planets are described as 'gas giants'?",
            options: [
              "Mercury, Venus, Earth, Mars",
              "Jupiter, Saturn, Uranus, Neptune",
              "Mars, Jupiter, Saturn, Uranus",
              "Earth, Mars, Jupiter, Saturn",
            ],
            correct: 1,
          },
          {
            id: 13,
            question:
              "The word 'exerting' in line 5 is closest in meaning to...",
            options: ["releasing", "applying", "reducing", "avoiding"],
            correct: 1,
          },
          {
            id: 14,
            question: "What keeps the planets in orbit around the Sun?",
            options: [
              "Solar wind",
              "Magnetic fields",
              "Gravitational pull",
              "Atmospheric pressure",
            ],
            correct: 2,
          },
          {
            id: 15,
            question:
              "According to the passage, which planets are rocky and small?",
            options: [
              "The outer planets",
              "The inner planets",
              "All planets",
              "Only Earth and Mars",
            ],
            correct: 1,
          },
        ],
      },

      {
        id: "passage4",
        type: "academic",
        title: "The Great Wall of China",
        passage: `The Great Wall of China is a series of fortifications built across the northern borders of China to protect against invasions. Construction began as early as the 7th century BC, but the most famous sections were built during the Ming Dynasty (1368–1644). The wall stretches over 13,000 miles, though not continuously. It was built using various materials, including earth, wood, and stone. Thousands of workers, including soldiers and peasants, labored on its construction.`,
        questions: [
          {
            id: 16,
            question: "What was the primary purpose of the Great Wall?",
            options: [
              "Trade route",
              "Defense against invasions",
              "Religious pilgrimage",
              "Agricultural boundary",
            ],
            correct: 1,
          },
          {
            id: 17,
            question: "During which dynasty were the most famous sections built?",
            options: [
              "Qin Dynasty",
              "Ming Dynasty",
              "Tang Dynasty",
              "Song Dynasty",
            ],
            correct: 1,
          },
          {
            id: 18,
            question:
              "The word 'fortifications' in line 1 is closest in meaning to...",
            options: ["palaces", "defensive structures", "temples", "bridges"],
            correct: 1,
          },
          {
            id: 19,
            question: "Approximately how long is the Great Wall?",
            options: [
              "5,000 miles",
              "10,000 miles",
              "13,000 miles",
              "20,000 miles",
            ],
            correct: 2,
          },
          {
            id: 20,
            question: "Who built the Great Wall?",
            options: [
              "Only soldiers",
              "Only peasants",
              "Soldiers and peasants",
              "Foreign laborers",
            ],
            correct: 2,
          },
        ],
      },

      {
        id: "passage5",
        type: "academic",
        title: "The Amazon Rainforest",
        passage: `The Amazon Rainforest is the largest tropical rainforest in the world, covering much of northwestern Brazil and extending into Colombia, Peru, and other South American countries. It is home to an immense biodiversity, including millions of species of insects, plants, birds, and mammals. The rainforest plays a critical role in regulating the global climate by absorbing carbon dioxide and producing oxygen. However, deforestation for agriculture and logging poses a serious threat to this ecosystem.`,
        questions: [
          {
            id: 21,
            question: "Where is the Amazon Rainforest primarily located?",
            options: [
              "Africa",
              "Southeast Asia",
              "South America",
              "Central America",
            ],
            correct: 2,
          },
          {
            id: 22,
            question:
              "What is the main threat to the Amazon Rainforest mentioned in the passage?",
            options: [
              "Climate change",
              "Deforestation",
              "Urbanization",
              "Pollution",
            ],
            correct: 1,
          },
          {
            id: 23,
            question:
              "The word 'immense' in line 3 is closest in meaning to...",
            options: ["small", "limited", "huge", "unknown"],
            correct: 2,
          },
          {
            id: 24,
            question:
              "According to the passage, what role does the Amazon play in the global climate?",
            options: [
              "It releases carbon dioxide.",
              "It absorbs carbon dioxide and produces oxygen.",
              "It increases global temperatures.",
              "It has no effect on climate.",
            ],
            correct: 1,
          },
          {
            id: 25,
            question: "What is the main idea of the passage?",
            options: [
              "The Amazon has many species of insects.",
              "The Amazon is important for biodiversity and climate, but is threatened.",
              "The Amazon is located only in Brazil.",
              "The Amazon produces most of the world's oxygen.",
            ],
            correct: 1,
          },
        ],
      },
    ],
  },
];

// ============================================================
// TIMER CONFIG
// ============================================================

const TEST_DURATION_SECONDS = 40 * 60;

// ============================================================
// ALWAYS JUMP TO THE VERY TOP OF THE PAGE
// ============================================================

const scrollToTop = () => {
  window.scrollTo(0, 0);
  if (document.documentElement) document.documentElement.scrollTop = 0;
  if (document.body) document.body.scrollTop = 0;
};

// ============================================================
// FORMAT TIMER
// ============================================================

function formatTime(totalSeconds) {
  const safeSeconds = Math.max(0, totalSeconds);
  const minutes = Math.floor(safeSeconds / 60);
  const seconds = safeSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

// ============================================================
// TIMER BADGE (inline — sits in the toolbar, never overlaps content)
// ============================================================

function TimerBadge({ timeLeft, isMobile }) {
  const isTimeLow = timeLeft <= 5 * 60;
  const isTimeCritical = timeLeft <= 60;

  return (
    <div
      style={{
        background: isTimeCritical
          ? "#fdeceb"
          : isTimeLow
          ? "#fff6e5"
          : "#eaf7f0",
        border: `1.5px solid ${
          isTimeCritical ? "#e05a4e" : isTimeLow ? "#e0a63c" : "#2e7d52"
        }`,
        borderRadius: "30px",
        padding: isMobile ? "5px 12px" : "6px 16px",
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        userSelect: "none",
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ fontSize: "0.8rem", fontWeight: "600", color: "#555" }}>
        Time Left
      </span>
      <span
        style={{
          fontSize: isMobile ? "0.95rem" : "1.05rem",
          fontWeight: "800",
          fontVariantNumeric: "tabular-nums",
          color: isTimeCritical
            ? "#c0392b"
            : isTimeLow
            ? "#a06a10"
            : "#1a5c3a",
        }}
      >
        {formatTime(timeLeft)}
      </span>
    </div>
  );
}

// ============================================================
// RESPONSIVE WINDOW WIDTH HOOK
// ============================================================

function useWindowWidth() {
  const [width, setWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );

  useEffect(() => {
    const handleResize = () => setWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return width;
}

// ============================================================
// MAIN COMPONENT
// ============================================================

function ToeflReading() {
  const navigate = useNavigate();
  const width = useWindowWidth();
  const isMobile = width < 768;

  const [selectedModule] = useState(readingModules[0].id);
  const currentModule = readingModules.find((m) => m.id === selectedModule);

  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [mcAnswers, setMcAnswers] = useState({});
  const [showPassage, setShowPassage] = useState(true);
  const [timeLeft, setTimeLeft] = useState(TEST_DURATION_SECONDS);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const hasAutoSubmittedRef = useRef(false);
  const isSubmittingRef = useRef(false);
  const calculateTotalScoreRef = useRef(null);

  // ----------------------------------------------------------
  // ALWAYS OPEN THE PAGE FROM THE VERY TOP
  // ----------------------------------------------------------

  useLayoutEffect(() => {
    const hasRestoration = "scrollRestoration" in window.history;
    const previousRestoration = hasRestoration
      ? window.history.scrollRestoration
      : null;

    if (hasRestoration) {
      window.history.scrollRestoration = "manual";
    }

    scrollToTop();
    const timeoutOne = setTimeout(scrollToTop, 0);
    const timeoutTwo = setTimeout(scrollToTop, 150);

    return () => {
      clearTimeout(timeoutOne);
      clearTimeout(timeoutTwo);
      if (hasRestoration) {
        window.history.scrollRestoration = previousRestoration;
      }
    };
  }, []);

  useEffect(() => {
    scrollToTop();
  }, [currentSectionIndex]);

  // ----------------------------------------------------------
  // CURRENT SECTION
  // ----------------------------------------------------------

  const currentSection = currentModule.sections[currentSectionIndex];
  const totalSections = currentModule.sections.length;
  const isLastSection = currentSectionIndex === totalSections - 1;
  const isLocked = isSubmitting || hasAutoSubmittedRef.current;

  const countAnswered = (sectionIndex) =>
    Object.keys(mcAnswers).filter((key) => key.startsWith(`${sectionIndex}-`))
      .length;

  const answeredCount = countAnswered(currentSectionIndex);
  const totalQuestions = currentModule.sections.reduce(
    (sum, s) => sum + s.questions.length,
    0
  );
  const totalAnswered = Object.keys(mcAnswers).length;

  // ==========================================================
  // NAVIGATION
  // ==========================================================

  const goToSection = (index) => {
    if (isLocked) return;
    if (index < 0 || index > totalSections - 1) return;
    setCurrentSectionIndex(index);
    setShowPassage(true);
  };

  const handleNext = () => goToSection(currentSectionIndex + 1);
  const handlePrev = () => goToSection(currentSectionIndex - 1);

  // ==========================================================
  // HANDLE ANSWER CHANGE
  // ==========================================================

  const handleMcChange = (sectionIndex, questionId, value) => {
    if (isSubmittingRef.current || hasAutoSubmittedRef.current) {
      return;
    }

    setMcAnswers((previous) => ({
      ...previous,
      [`${sectionIndex}-${questionId}`]: value,
    }));
  };

  // ==========================================================
  // TOEFL SCORE CONVERSION
  // ==========================================================

  const convertToToeflScore = useCallback((correct) => {
    if (correct >= 25) return 30;
    if (correct >= 23) return 29;
    if (correct >= 21) return 28;
    if (correct >= 19) return 26;
    if (correct >= 17) return 24;
    if (correct >= 15) return 22;
    if (correct >= 13) return 20;
    if (correct >= 10) return 17;
    if (correct >= 7) return 14;
    return 10;
  }, []);

  // ==========================================================
  // CALCULATE + SAVE RESULT
  // ==========================================================

  const calculateTotalScore = useCallback(async () => {
    if (isSubmittingRef.current) {
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);

    let correctCount = 0;
    let total = 0;
    const breakdown = [];

    currentModule.sections.forEach((section, sectionIndex) => {
      section.questions.forEach((question) => {
        total++;

        const answerKey = `${sectionIndex}-${question.id}`;
        const rawAnswer = mcAnswers[answerKey];

        const userIndex =
          rawAnswer !== undefined
            ? Number.parseInt(rawAnswer, 10)
            : Number.NaN;

        const isCorrect = userIndex === question.correct;

        if (isCorrect) {
          correctCount++;
        }

        const userAnswerText =
          rawAnswer === undefined
            ? "No answer"
            : question.options[userIndex] ?? "Invalid answer";

        const correctAnswerText = question.options[question.correct];

        const entry = {
          number: question.id,
          questionText: question.question,
          userAnswerText,
          correctAnswerText,
          isCorrect,
        };

        if (question.explanation) {
          entry.explanation = question.explanation;
        }

        breakdown.push(entry);
      });
    });

    const percentage =
      total > 0 ? Math.round((correctCount / total) * 100) : 0;

    const toeflScore = convertToToeflScore(correctCount);
    const API_URL = getApiBaseUrl();

    try {
      const response = await fetch(`${API_URL}/tests/save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          testName: "TOEFL Reading Test",
          result: {
            rawScore: correctCount,
            total,
            percentage,
            toeflScore,
            breakdown,
          },
        }),
      });

      let savedData = null;
      const contentType = response.headers.get("content-type");

      if (contentType && contentType.includes("application/json")) {
        savedData = await response.json();
      } else {
        savedData = await response.text();
      }

      if (!response.ok) {
        console.error("Failed to save TOEFL result:", savedData);
        alert("Failed to save your result. Please try again.");
        isSubmittingRef.current = false;
        setIsSubmitting(false);
        return;
      }

      navigate("/quiz/toefl/result", {
        state: {
          correctCount,
          total,
          percentage,
          toeflScore,
          testType: "Reading (PBT)",
          breakdown,
          savedData,
        },
      });
    } catch (error) {
      console.error("Error saving TOEFL result:", error);
      alert(
        "Unable to save your result. Please check your internet connection and try again."
      );
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [currentModule, mcAnswers, convertToToeflScore, navigate]);

  useEffect(() => {
    calculateTotalScoreRef.current = calculateTotalScore;
  }, [calculateTotalScore]);

  // ==========================================================
  // 40-MINUTE COUNTDOWN TIMER
  // ==========================================================

  useEffect(() => {
    if (hasAutoSubmittedRef.current) {
      return;
    }

    const intervalId = setInterval(() => {
      setTimeLeft((previousTime) => {
        if (previousTime <= 1) {
          clearInterval(intervalId);
          return 0;
        }
        return previousTime - 1;
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    if (timeLeft === 0 && !hasAutoSubmittedRef.current) {
      hasAutoSubmittedRef.current = true;
      if (calculateTotalScoreRef.current) {
        calculateTotalScoreRef.current();
      }
    }
  }, [timeLeft]);

  // ==========================================================
  // FINISH / EXIT
  // ==========================================================

  const handleFinish = () => {
    if (isSubmitting || hasAutoSubmittedRef.current) {
      return;
    }

    const unanswered = totalQuestions - totalAnswered;

    if (unanswered > 0) {
      const ok = window.confirm(
        `You have ${unanswered} unanswered question${
          unanswered === 1 ? "" : "s"
        }. Submit anyway?`
      );
      if (!ok) return;
    }

    hasAutoSubmittedRef.current = true;
    calculateTotalScore();
  };

  const handleExit = () => {
    if (isSubmitting) {
      return;
    }
    navigate("/tests");
  };

  // ==========================================================
  // SHARED STYLES
  // ==========================================================

  // Both panes share this height on desktop so the page fits one screen
  const paneHeight = isMobile ? "auto" : "calc(100vh - 300px)";

  const paneStyle = {
    background: "#ffffff",
    border: "1px solid #c8e6d4",
    borderRadius: "12px",
    boxSizing: "border-box",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    height: paneHeight,
    minHeight: isMobile ? 0 : "380px",
  };

  const paneHeaderStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "8px",
    padding: isMobile ? "10px 14px" : "12px 20px",
    background: "#f4faf7",
    borderBottom: "1px solid #dceee5",
    flexShrink: 0,
  };

  const paneTitleStyle = {
    margin: 0,
    fontSize: isMobile ? "0.95rem" : "1.05rem",
    fontWeight: "700",
    color: "#1a5c3a",
  };

  const paneBodyStyle = {
    flex: 1,
    minHeight: 0,
    overflowY: isMobile ? "visible" : "auto",
    padding: isMobile ? "14px" : "20px 24px",
  };

  const pillStyle = {
    background: "#eaf7f0",
    border: "1px solid #c8e6d4",
    color: "#2e7d52",
    fontWeight: "600",
    fontSize: "0.78rem",
    borderRadius: "6px",
    padding: "3px 10px",
    whiteSpace: "nowrap",
  };

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <div
      style={{
        maxWidth: "1400px",
        margin: "0 auto",
        padding: isMobile ? "84px 12px 130px" : "96px 24px 20px",
        background: "#ffffff",
        color: "#111",
        boxSizing: "border-box",
      }}
    >
      {/* =====================================================
          TOOLBAR: breadcrumb (left) — progress + timer (right)
      ====================================================== */}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "10px",
          marginBottom: "10px",
        }}
      >
        <div
          style={{
            fontSize: isMobile ? "1.1rem" : "1.4rem",
            color: "#555",
          }}
        >
          TOEFL Suite /{" "}
          <span style={{ color: "#2e7d52", fontWeight: "600" }}>Reading</span>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span style={{ ...pillStyle, padding: "5px 12px" }}>
            {totalAnswered}/{totalQuestions} answered
          </span>

          {!isMobile && <TimerBadge timeLeft={timeLeft} isMobile={false} />}
        </div>
      </div>

      {/* =====================================================
          INSTRUCTIONS (compact strip)
      ====================================================== */}

      <div
        style={{
          background: "#f4faf7",
          border: "1px solid #c8e6d4",
          borderLeft: "4px solid #2e7d52",
          borderRadius: "10px",
          padding: isMobile ? "8px 12px" : "8px 16px",
          marginBottom: "10px",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          columnGap: "18px",
          rowGap: "2px",
          fontSize: isMobile ? "0.78rem" : "0.84rem",
          color: "#444",
          lineHeight: "1.6",
        }}
      >
        <strong style={{ color: "#1a5c3a" }}>Instructions:</strong>
        <span>Read each passage carefully and answer all questions.</span>
        <span>
          {isMobile
            ? "Use the tabs to switch between passage and questions."
            : "Use Previous / Next to move between passages."}
        </span>
        <span>
          40 minutes in total — the test submits automatically when time runs
          out.
        </span>
      </div>

      {/* =====================================================
          MOBILE TABS
      ====================================================== */}

      {isMobile && (
        <div
          style={{
            display: "flex",
            background: "#eaf7f0",
            borderRadius: "10px",
            padding: "4px",
            marginBottom: "10px",
            gap: "4px",
          }}
        >
          {["Passage", "Questions"].map((label, index) => {
            const active =
              (index === 0 && showPassage) || (index === 1 && !showPassage);

            return (
              <button
                key={label}
                type="button"
                onClick={() => setShowPassage(index === 0)}
                style={{
                  flex: 1,
                  padding: "8px",
                  borderRadius: "8px",
                  border: "none",
                  background: active ? "#2e7d52" : "transparent",
                  color: active ? "#fff" : "#2e7d52",
                  fontWeight: "700",
                  fontSize: "0.87rem",
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
              >
                {label}{" "}
                {index === 1 &&
                  `(${answeredCount}/${currentSection.questions.length})`}
              </button>
            );
          })}
        </div>
      )}

      {/* =====================================================
          TWO EQUAL-HEIGHT PANES (each scrolls on its own)
      ====================================================== */}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
          gap: "14px",
          marginBottom: "10px",
        }}
      >
        {/* ---------------- PASSAGE ---------------- */}
        {(!isMobile || showPassage) && (
          <div style={paneStyle}>
            <div style={paneHeaderStyle}>
              <h2 style={paneTitleStyle}>
                Passage {currentSectionIndex + 1}: {currentSection.title}
              </h2>
              <span style={pillStyle}>
                Q{currentSection.questions[0].id}–
                {
                  currentSection.questions[currentSection.questions.length - 1]
                    .id
                }
              </span>
            </div>

            <div style={paneBodyStyle}>
              <div
                style={{
                  lineHeight: "1.9",
                  fontSize: isMobile ? "0.95rem" : "1.02rem",
                  color: "#222",
                  whiteSpace: "pre-wrap",
                }}
              >
                {currentSection.passage}
              </div>
            </div>
          </div>
        )}

        {/* ---------------- QUESTIONS ---------------- */}
        {(!isMobile || !showPassage) && (
          <div style={paneStyle}>
            <div style={paneHeaderStyle}>
              <h3 style={paneTitleStyle}>Questions</h3>
              <span style={pillStyle}>
                {answeredCount}/{currentSection.questions.length} answered
              </span>
            </div>

            <div style={paneBodyStyle}>
              {currentSection.questions.map((question, qIndex) => {
                const isLastQuestion =
                  qIndex === currentSection.questions.length - 1;

                return (
                  <div
                    key={question.id}
                    style={{
                      marginBottom: isLastQuestion ? 0 : "1.1rem",
                      paddingBottom: isLastQuestion ? 0 : "1rem",
                      borderBottom: isLastQuestion
                        ? "none"
                        : "1px solid #e8f5ee",
                    }}
                  >
                    <p
                      style={{
                        fontWeight: "600",
                        margin: "0 0 8px 0",
                        color: "#111",
                        fontSize: isMobile ? "0.92rem" : "0.97rem",
                        lineHeight: "1.5",
                      }}
                    >
                      {question.id}. {question.question}
                    </p>

                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "6px",
                      }}
                    >
                      {question.options.map((option, optionIndex) => {
                        const selected =
                          mcAnswers[`${currentSectionIndex}-${question.id}`] ===
                          optionIndex.toString();

                        return (
                          <label
                            key={optionIndex}
                            style={{
                              display: "flex",
                              alignItems: "flex-start",
                              gap: "10px",
                              cursor: isLocked ? "not-allowed" : "pointer",
                              padding: isMobile ? "10px" : "8px 12px",
                              borderRadius: "8px",
                              background: selected ? "#e8f5ee" : "#fafffe",
                              border: selected
                                ? "1.5px solid #2e7d52"
                                : "1.5px solid #dceee5",
                              transition: "all 0.15s",
                              fontSize: isMobile ? "0.88rem" : "0.92rem",
                              color: "#222",
                              lineHeight: "1.5",
                              boxSizing: "border-box",
                            }}
                          >
                            <input
                              type="radio"
                              name={`q-${currentSectionIndex}-${question.id}`}
                              value={optionIndex}
                              checked={selected}
                              disabled={isLocked}
                              onChange={(event) =>
                                handleMcChange(
                                  currentSectionIndex,
                                  question.id,
                                  event.target.value
                                )
                              }
                              style={{
                                accentColor: "#2e7d52",
                                width: "16px",
                                height: "16px",
                                flexShrink: 0,
                                marginTop: "3px",
                              }}
                            />

                            <span>
                              <span
                                style={{
                                  color: "#2e7d52",
                                  fontWeight: "700",
                                  marginRight: "6px",
                                }}
                              >
                                {String.fromCharCode(65 + optionIndex)}.
                              </span>
                              {option}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* =====================================================
          BOTTOM NAVIGATION
      ====================================================== */}

      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "stretch" : "center",
          gap: isMobile ? "10px" : "0",
          background: "#f9fefb",
          border: "1px solid #c8e6d4",
          borderRadius: isMobile ? "12px 12px 0 0" : "10px",
          padding: isMobile ? "10px 12px" : "8px 16px",
          ...(isMobile
            ? {
                position: "fixed",
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 100,
                boxShadow: "0 -2px 12px rgba(0,0,0,0.08)",
              }
            : {}),
        }}
      >
        {/* EXIT + COUNTER (+ timer on mobile) */}
        <div
          style={{
            display: "flex",
            justifyContent: isMobile ? "space-between" : "flex-start",
            alignItems: "center",
            gap: "14px",
            order: isMobile ? 2 : 1,
          }}
        >
          <button
            type="button"
            onClick={handleExit}
            disabled={isSubmitting}
            style={{
              padding: "7px 18px",
              background: "transparent",
              border: "1px solid #c8e6d4",
              color: isSubmitting ? "#aaa" : "#555",
              borderRadius: "30px",
              cursor: isSubmitting ? "not-allowed" : "pointer",
              fontSize: "0.86rem",
            }}
          >
            Exit Test
          </button>

          {isMobile && <TimerBadge timeLeft={timeLeft} isMobile />}

          {!isMobile && (
            <span style={{ fontSize: "0.84rem", color: "#555" }}>
              Passage{" "}
              <strong style={{ color: "#1a5c3a" }}>
                {currentSectionIndex + 1}
              </strong>{" "}
              of {totalSections}
            </span>
          )}
        </div>

        {/* PREV / DOTS / NEXT */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "6px",
            flexWrap: "wrap",
            order: isMobile ? 1 : 2,
          }}
        >
          <button
            type="button"
            onClick={handlePrev}
            disabled={currentSectionIndex === 0 || isSubmitting}
            style={{
              padding: isMobile ? "8px 14px" : "7px 16px",
              background: currentSectionIndex === 0 ? "#f0f0f0" : "#ffffff",
              color: currentSectionIndex === 0 ? "#aaa" : "#2e7d52",
              border: "1px solid #c8e6d4",
              borderRadius: "30px",
              cursor:
                currentSectionIndex === 0 || isSubmitting
                  ? "not-allowed"
                  : "pointer",
              fontWeight: "600",
              fontSize: "0.86rem",
            }}
          >
            ← Prev
          </button>

          {Array.from({ length: totalSections }).map((_, index) => {
            const isCurrent = index === currentSectionIndex;
            const isComplete =
              countAnswered(index) === currentModule.sections[index].questions.length;

            return (
              <button
                key={index}
                type="button"
                onClick={() => goToSection(index)}
                disabled={isSubmitting}
                title={`Passage ${index + 1}${isComplete ? " (completed)" : ""}`}
                style={{
                  width: isMobile ? "28px" : "30px",
                  height: isMobile ? "28px" : "30px",
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.78rem",
                  fontWeight: "700",
                  padding: 0,
                  cursor: isSubmitting ? "not-allowed" : "pointer",
                  background: isCurrent
                    ? "#2e7d52"
                    : isComplete
                    ? "#cfeedd"
                    : "#ffffff",
                  color: isCurrent ? "#fff" : "#2e7d52",
                  border: `1.5px solid ${
                    isCurrent || isComplete ? "#2e7d52" : "#c8e6d4"
                  }`,
                  flexShrink: 0,
                }}
              >
                {index + 1}
              </button>
            );
          })}

          {!isLastSection ? (
            <button
              type="button"
              onClick={handleNext}
              disabled={isSubmitting}
              style={{
                padding: isMobile ? "8px 14px" : "7px 16px",
                background: isSubmitting ? "#9ab9aa" : "#2e7d52",
                color: "#fff",
                border: "none",
                borderRadius: "30px",
                cursor: isSubmitting ? "not-allowed" : "pointer",
                fontWeight: "600",
                fontSize: "0.86rem",
              }}
            >
              Next →
            </button>
          ) : (
            <button
              type="button"
              onClick={handleFinish}
              disabled={isSubmitting}
              style={{
                padding: isMobile ? "8px 16px" : "7px 18px",
                background: isSubmitting ? "#789989" : "#1a5c3a",
                color: "#fff",
                border: "none",
                borderRadius: "30px",
                cursor: isSubmitting ? "not-allowed" : "pointer",
                fontWeight: "700",
                fontSize: "0.86rem",
                minWidth: "85px",
              }}
            >
              {isSubmitting ? "Saving..." : "Finish ✓"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
export default ToeflReading;